// =============================================================================
// ClientPortalsScreen — /client-portals  (T-417)
// Company-wide view of every client HOP portal link — James: "a better place
// to manage and see client portals would be nice, currently nested in
// project → HOP library → Client portal". One table: project, client, what's
// been sent, days left, copy/open/extend/revoke. Manager+.
// =============================================================================

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Share2, Copy, Check, ExternalLink, CalendarClock, Trash2, AlertTriangle,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState, KPI } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

const EXTEND_DAYS = 30;

export default function ClientPortalsScreen() {
  const user = useRequireAuth();
  const canView = hasRole(user, "manager");
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState<string | null>(null);

  const { data: shares = [], isLoading } = useQuery({
    queryKey: ["client_portals_all"],
    enabled: !!user && canView,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_shares")
        .select("share_id, token, project_id, expires_at, revoked_at, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as any[];
      // Hydrate projects + clients (house .in() pattern)
      const pids = [...new Set(rows.map(r => r.project_id).filter(Boolean))];
      if (pids.length) {
        const { data: ps } = await supabase.from("projects")
          .select("project_id, project_code, project_name, clients(legal_name, trading_name)")
          .in("project_id", pids);
        const by = new Map((ps ?? []).map((p: any) => [p.project_id, p]));
        rows.forEach(r => { r._proj = by.get(r.project_id) ?? null; });
      }
      // Live batch counts + last release
      const sids = rows.map(r => r.share_id);
      if (sids.length) {
        const { data: bs } = await supabase.from("hop_share_batches")
          .select("share_id, created_at")
          .in("share_id", sids)
          .is("revoked_at", null);
        const agg = new Map<string, { n: number; last: string }>();
        for (const b of (bs ?? []) as any[]) {
          const cur = agg.get(b.share_id) ?? { n: 0, last: b.created_at };
          cur.n += 1;
          if (b.created_at > cur.last) cur.last = b.created_at;
          agg.set(b.share_id, cur);
        }
        rows.forEach(r => { const a = agg.get(r.share_id); r._batches = a?.n ?? 0; r._last = a?.last ?? null; });
      }
      return rows;
    },
  });

  const live = useMemo(() => shares.filter((s: any) => {
    if (s.revoked_at) return false;
    return !s.expires_at || new Date(s.expires_at).getTime() > Date.now();
  }), [shares]);
  const dead = useMemo(() => shares.filter((s: any) => !live.includes(s)), [shares, live]);

  async function copyLink(s: any) {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/hop-view/${s.token}`);
      setCopied(s.share_id);
      setTimeout(() => setCopied(null), 1500);
    } catch { window.prompt("Copy link:", `${window.location.origin}/hop-view/${s.token}`); }
  }
  async function extend(s: any) {
    await supabase.from("hop_shares")
      .update({ expires_at: new Date(Date.now() + EXTEND_DAYS * 86400000).toISOString() })
      .eq("share_id", s.share_id);
    queryClient.invalidateQueries({ queryKey: ["client_portals_all"] });
  }
  async function revoke(s: any) {
    if (!confirm(`Revoke the portal for ${s._proj?.project_code ?? "this project"}? The client's link stops working immediately.`)) return;
    await supabase.from("hop_shares")
      .update({ revoked_at: new Date().toISOString() })
      .eq("share_id", s.share_id);
    queryClient.invalidateQueries({ queryKey: ["client_portals_all"] });
  }

  if (!user) return null;
  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><EmptyState icon={Share2} title="Managers only" description="Client portals are managed by managers and directors." /></Card>
      </div>
    );
  }

  const daysLeft = (s: any) =>
    s.expires_at ? Math.ceil((new Date(s.expires_at).getTime() - Date.now()) / 86400000) : null;

  return (
    <div className="p-4 md:p-6 max-w-[1000px] mx-auto pb-20">
      <div className="mb-5">
        <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Share2 size={20} className="text-accent" /> Client portals
        </h1>
        <p className="text-text-muted text-[13px] mt-1">
          Every live HOP photo link across the company — copy, open, extend or kill them from one place.
          New portals are created from a project's HOP library.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <KPI label="Live portals" value={live.length} icon={Share2} />
        <KPI label="Expiring ≤ 7 days" value={live.filter((s: any) => { const d = daysLeft(s); return d != null && d <= 7; }).length}
             icon={CalendarClock} accent={live.some((s: any) => { const d = daysLeft(s); return d != null && d <= 7; })} />
        <KPI label="Expired / revoked" value={dead.length} />
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : live.length === 0 ? (
        <Card><EmptyState icon={Share2} title="No live portals" description="Open a project's HOP library → Client portal to create one." /></Card>
      ) : (
        <div className="space-y-2">
          {live.map((s: any) => {
            const d = daysLeft(s);
            return (
              <Card key={s.share_id} className={d != null && d <= 7 ? "border-warn/40" : ""}>
                <div className="p-3.5 flex items-center gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/projects/${s.project_id}/hop`} className="text-[14px] font-medium text-text hover:text-accent">
                        {s._proj?.project_code ?? "—"} · {s._proj?.project_name ?? "Unknown project"}
                      </Link>
                      {d != null && <Pill label={`${d}d left`} tone={d <= 7 ? "warning" : "default"} small />}
                    </div>
                    <div className="text-[11.5px] text-text-muted mt-0.5">
                      {s._proj?.clients?.trading_name || s._proj?.clients?.legal_name || "—"}
                      {" · "}{s._batches ?? 0} update{(s._batches ?? 0) === 1 ? "" : "s"} sent
                      {s._last && <> · last {fmtDate(s._last)}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Button size="sm" variant="secondary" icon={copied === s.share_id ? Check : Copy} onClick={() => copyLink(s)}>
                      {copied === s.share_id ? "Copied" : "Copy link"}
                    </Button>
                    <a href={`/hop-view/${s.token}`} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="ghost" icon={ExternalLink}>Open</Button>
                    </a>
                    <Button size="sm" variant="ghost" icon={CalendarClock} onClick={() => extend(s)} title={`Extend ${EXTEND_DAYS} days`}>
                      Extend
                    </Button>
                    <Button size="sm" variant="ghost" icon={Trash2} onClick={() => revoke(s)} title="Kill this link">
                      Revoke
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {dead.length > 0 && (
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-2 px-1">
            Expired / revoked
          </div>
          <div className="space-y-1.5">
            {dead.slice(0, 10).map((s: any) => (
              <div key={s.share_id} className="px-3 py-2 rounded border border-line/50 text-[12px] text-text-muted flex items-center gap-2">
                <AlertTriangle size={12} className="text-text-dim" />
                {s._proj?.project_code ?? "—"} · {s.revoked_at ? `revoked ${fmtDate(s.revoked_at)}` : `expired ${fmtDate(s.expires_at)}`}
                <Link to={`/projects/${s.project_id}/hop`} className="ml-auto text-accent hover:underline">re-issue from HOP library</Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
